"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.usePatientGroupForm = void 0;
const react_1 = require("react");
const dayjs_1 = __importDefault(require("dayjs"));
const domain_1 = require("@home-health-notify/domain");
const lodash_1 = require("lodash");
const isEmpty_1 = __importDefault(require("lodash/isEmpty"));
const LayoutHelper_1 = require("../utils/LayoutHelper");
const ValidationHelper_1 = require("../utils/ValidationHelper");
const patientGroupFormUtils_1 = require("../utils/patientGroupFormUtils");
const EMPTY_GROUP_INFO = {};
const usePatientGroupForm = ({ mode, patientGroupInfo = EMPTY_GROUP_INFO, }) => {
    var _a;
    const [errors, setErrors] = (0, react_1.useState)(patientGroupFormUtils_1.defaultErrors);
    const [updatedPatientGroupData, setUpdatedPatientGroupData] = (0, react_1.useState)((0, patientGroupFormUtils_1.mapUpdatedGroupInfoToPatientInfo)(patientGroupInfo));
    const [patientStatus, setPatientStatus] = (0, react_1.useState)((_a = patientGroupInfo === null || patientGroupInfo === void 0 ? void 0 : patientGroupInfo.patientStatus) !== null && _a !== void 0 ? _a : +domain_1.PATIENT_STATUS.PENDING_SOC);
    const [socStarter, setSocStarterState] = (0, react_1.useState)((0, LayoutHelper_1.getSocStarterValue)(patientGroupInfo === null || patientGroupInfo === void 0 ? void 0 : patientGroupInfo.socStarter));
    const [lupaData, setLupaData] = (0, react_1.useState)((0, patientGroupFormUtils_1.mapGroupInfoToILupaData)(patientGroupInfo !== null && patientGroupInfo !== void 0 ? patientGroupInfo : {}));
    const [referralInfo, setReferralInfo] = (0, react_1.useState)((0, patientGroupFormUtils_1.mapGroupInfoToIReferralInfo)(patientGroupInfo !== null && patientGroupInfo !== void 0 ? patientGroupInfo : {}));
    // Initialize/populate form from patientGroupInfo based on mode
    (0, react_1.useEffect)(() => {
        if (mode === "new") {
            if ((0, isEmpty_1.default)(patientGroupInfo)) {
                initializeDefaultFields();
            }
            else {
                setUpdatedPatientGroupData((0, patientGroupFormUtils_1.mapUpdatedGroupInfoToPatientInfo)(patientGroupInfo));
                setReferralInfo((0, patientGroupFormUtils_1.mapGroupInfoToIReferralInfo)(patientGroupInfo));
                if (patientGroupInfo.patientStatus !== undefined) {
                    setPatientStatus(patientGroupInfo.patientStatus);
                }
                setSocStarterState((0, LayoutHelper_1.getSocStarterValue)(patientGroupInfo.socStarter));
                setLupaData({
                    firstSession: 0,
                    secondSession: 0,
                });
            }
        }
        else if (patientGroupInfo) {
            setUpdatedPatientGroupData((0, patientGroupFormUtils_1.mapUpdatedGroupInfoToPatientInfo)(patientGroupInfo));
            setReferralInfo((0, patientGroupFormUtils_1.mapGroupInfoToIReferralInfo)(patientGroupInfo));
            setPatientStatus(patientGroupInfo.patientStatus);
            setSocStarterState((0, LayoutHelper_1.getSocStarterValue)(patientGroupInfo.socStarter));
            if (mode === "edit") {
                setLupaData((0, patientGroupFormUtils_1.mapGroupInfoToILupaData)(patientGroupInfo));
            }
            else {
                setLupaData({
                    firstSession: 0,
                    secondSession: 0,
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, patientGroupInfo]);
    // Reset to defaults for new mode when no patient info
    (0, react_1.useEffect)(() => {
        if (mode === "new" && !(patientGroupInfo === null || patientGroupInfo === void 0 ? void 0 : patientGroupInfo.patientInfo)) {
            initializeDefaultFields();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, patientGroupInfo]);
    // ─── Field Change Handlers ───────────────────────────────────────────────
    const onSetStatus = (status) => {
        setPatientStatus(status);
    };
    const setSocStarter = (socVal) => {
        setSocStarterState(socVal);
    };
    const onSetErrors = (key, value) => {
        setErrors(Object.assign(Object.assign({}, errors), { [key]: value }));
    };
    const onFormDataChange = (key, value) => {
        setUpdatedPatientGroupData(() => {
            const newPatientInfo = Object.assign({}, updatedPatientGroupData);
            if (key === "placeName" || key === "apartmentNo") {
                if (typeof value === "string") {
                    newPatientInfo.address = Object.assign(Object.assign({}, newPatientInfo.address), { [key]: value });
                }
                else {
                    newPatientInfo.address = Object.assign(Object.assign({}, newPatientInfo.address), value);
                }
            }
            else {
                return Object.assign(Object.assign({}, newPatientInfo), { [key]: value });
            }
            return newPatientInfo;
        });
    };
    const onDobChange = (dob) => {
        setUpdatedPatientGroupData(Object.assign(Object.assign({}, updatedPatientGroupData), { dob }));
        const date = (0, patientGroupFormUtils_1.dobStringToDate)(dob);
        onSetErrors("dob", !date || !(0, ValidationHelper_1.isValidDateOfBirth)((0, dayjs_1.default)(date)));
    };
    const onLupaDataChange = (key, value) => {
        setLupaData(() => {
            return Object.assign(Object.assign({}, lupaData), { [key]: value });
        });
    };
    const onReferralInfoChange = (key, value) => {
        setReferralInfo(() => {
            return Object.assign(Object.assign({}, referralInfo), { [key]: value });
        });
    };
    const onAddInfoDataChange = (key, value) => {
        setUpdatedPatientGroupData(Object.assign(Object.assign({}, updatedPatientGroupData), { info: Object.assign(Object.assign({}, updatedPatientGroupData.info), { [key]: value }) }));
    };
    const onSetAbout = (about) => {
        setUpdatedPatientGroupData(Object.assign(Object.assign({}, updatedPatientGroupData), { about }));
    };
    const onSetInsurance = (insurance) => {
        setUpdatedPatientGroupData(Object.assign(Object.assign({}, updatedPatientGroupData), { insurance }));
    };
    const onSetMeta = (meta) => {
        setUpdatedPatientGroupData(Object.assign(Object.assign({}, updatedPatientGroupData), { meta }));
    };
    // ─── Validation ──────────────────────────────────────────────────────────
    const validateMandatoryFields = () => {
        const isError = Object.values(errors).find(error => error);
        // MRN is optional for now (form UX pending); users may enter it and it
        // saves, but it never blocks create/edit. See #1474.
        return ((0, patientGroupFormUtils_1.validateFormDataLength)(updatedPatientGroupData) ||
            !(0, patientGroupFormUtils_1.validateAddInfo)(updatedPatientGroupData.info) ||
            !!isError);
    };
    const isFormInvalid = validateMandatoryFields();
    const validateAllFields = () => {
        const newErrors = {
            firstName: !updatedPatientGroupData.firstName.trim(),
            lastName: !updatedPatientGroupData.lastName.trim(),
            mrn: false,
            dob: !updatedPatientGroupData.dob,
            phoneNumber: !(0, patientGroupFormUtils_1.isValidPhoneNo)(updatedPatientGroupData.phoneNo),
            address: !updatedPatientGroupData.address.placeName.trim(),
        };
        setErrors(newErrors);
        const hasErrors = Object.values(newErrors).some(e => e);
        return !hasErrors && (0, patientGroupFormUtils_1.validateAddInfo)(updatedPatientGroupData.info);
    };
    // ─── Submit Preparation ──────────────────────────────────────────────────
    const buildPayload = () => {
        return (0, patientGroupFormUtils_1.buildPatientGroupPayload)(updatedPatientGroupData, patientStatus, socStarter, lupaData, referralInfo);
    };
    const initializeDefaultFields = () => {
        setUpdatedPatientGroupData((0, lodash_1.cloneDeep)(patientGroupFormUtils_1.defaultPatientInfoData));
        setPatientStatus(+domain_1.PATIENT_STATUS.PENDING_SOC);
        setSocStarterState((0, LayoutHelper_1.getSocStarterValue)({ SN: false, PT: false, SLP: false, OT: false }));
        setLupaData({
            firstSession: 0,
            secondSession: 0,
        });
        setReferralInfo({
            referralSource: "",
            communityLiaison: "",
            attachments: [],
        });
    };
    return {
        updatedPatientGroupData,
        patientStatus,
        socStarter,
        lupaData,
        referralInfo,
        errors,
        onFormDataChange,
        onDobChange,
        onSetStatus,
        setSocStarter,
        onLupaDataChange,
        onReferralInfoChange,
        onAddInfoDataChange,
        onSetAbout,
        onSetInsurance,
        onSetMeta,
        onSetErrors,
        setUpdatedPatientGroupData,
        isFormInvalid,
        validateMandatoryFields,
        validateAllFields,
        buildPayload,
        initializeDefaultFields,
    };
};
exports.usePatientGroupForm = usePatientGroupForm;
